/**
 * Registered with another program (If user is trying to register with SPP they are already registered with TPP and vice versa)
 * Not applicable for DC
 */
import React, { useMemo } from "react";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DxpPageShell from "./Common/StandaloneFormViews/DxpPageShell";
import {
  getUserData,
  getDXPRegistrationLink,
  getSupportCaseLink,
  createDxpClickHandler,
} from "../Helpers/utils";
import { setPartnerRegistrationStatus } from "../Redux/actions";
import { PROGRAM_SPECIFIC_NAMES } from "../Helpers/constants";
import {
  DxpBodyText,
  DxpHeading,
  DxpPrimaryButton,
  DxpNarrowColumn,
  DxpMessageCard,
  DxpFooterRow,
} from "./dxpStandaloneUi";
import DxpRegistrationTopHeader from "./DxpRegistrationTopHeader";

/** Redux store uses thunk middleware; dispatch must accept thunk action creators. */
export type AlreadyRegisteredThunkDispatch = ThunkDispatch<
  unknown,
  undefined,
  AnyAction
>;

type AlreadyRegisteredHandlersDeps = {
  dispatch: AlreadyRegisteredThunkDispatch;
  navigate: ReturnType<typeof useNavigate>;
};

const PROGRAM = "DXP";

/** Exported for unit tests and parity with template-driven action types. */
export function createAlreadyRegisteredWithAnotherProgramHandlers({
  dispatch,
  navigate,
}: AlreadyRegisteredHandlersDeps) {
  return {
    newCompany: () => {
      localStorage.setItem(
        "RedirectPath",
        PROGRAM_SPECIFIC_NAMES[PROGRAM].route.registration,
      );
      localStorage.removeItem("emailMismatch");
      localStorage.removeItem("partnerRegistrationFormStep");
      const userData = getUserData();
      const email = userData?.email || "";
      if (email) localStorage.setItem("email", email);
      window.open(getDXPRegistrationLink(), "_self");
    },
    continueRegistration: () => {
      localStorage.setItem(
        "RedirectPath",
        PROGRAM_SPECIFIC_NAMES[PROGRAM].route.registration,
      );
      localStorage.removeItem("emailMismatch");
      localStorage.removeItem("partnerRegistrationFormStep");
      const userData = getUserData();
      const email = userData?.email || "";
      if (email) localStorage.setItem("email", email);
      window.open(getDXPRegistrationLink(), "_self");
    },
    goBackToMxpTppRegister: () => {
      dispatch(setPartnerRegistrationStatus("RegisteredWithAnotherProgram", 0));
      const userData = getUserData();
      const email = userData?.email || "";
      localStorage.removeItem("emailMismatch");
      localStorage.removeItem("partnerRegistrationFormStep");
      if (email) {
        localStorage.setItem("email", email);
        window.open(getDXPRegistrationLink(), "_self");
      } else {
        window.open(getDXPRegistrationLink(), "_self");
      }
    },
    goBackToMxpSppRegister: () => {
      dispatch(setPartnerRegistrationStatus("RegisteredWithAnotherProgram", 0));
      const userData = getUserData();
      const email = userData?.email || "";
      if (email) {
        localStorage.setItem("email", email);
        navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.registration);
      } else {
        navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.registration);
      }
    },
    goToRegistrationHome: () => {
      navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.registration);
    },
    email: () => {
      window.open(getSupportCaseLink(), "_blank");
    },
    chat: () => {
      window.feds?.services?.jarvis?.openChat();
    },
  };
}

function AlreadyRegisteredWithAnotherProgram() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AlreadyRegisteredThunkDispatch>();

  const customHandlers = useMemo(
    () =>
      createAlreadyRegisteredWithAnotherProgramHandlers({ dispatch, navigate }),
    [dispatch, navigate],
  );

  const onAction = useMemo(
    () => createDxpClickHandler(customHandlers),
    [customHandlers],
  );

  const email = getUserData()?.email || localStorage.getItem("email") || "";
  const alreadyRegisteredWithLabel =
    localStorage.getItem("alreadyRegisteredWith") || "";
  const { title } = PROGRAM_SPECIFIC_NAMES.DXP.partnerProgram;

  return (
    <>
      <DxpRegistrationTopHeader program={PROGRAM} />

      <DxpPageShell customHandlers={customHandlers} requireCommonHeaders>
        <div className="dxp-static-message-outer">
          <DxpNarrowColumn>
            <DxpMessageCard className="dxp-message-card--min-h-350">
              <DxpHeading className="dxp-heading--fs-18">
                You are already registered with another Adobe partner program
              </DxpHeading>

              <DxpBodyText className="dxp-body-text--mb-32">
                The email address you entered {email} is currently being used by
                another partner program:{" "}
                <span className="dxp-text-accent-inline">
                  {alreadyRegisteredWithLabel}
                </span>
                .
              </DxpBodyText>
              <DxpBodyText className="dxp-body-text--mb-32 dxp-body-text--bold">
                Continue the registration with this email address.
              </DxpBodyText>

              <DxpFooterRow>
                <DxpPrimaryButton
                  label={`Apply to join the ${title}`}
                  onPress={() => onAction({ actionType: "newCompany" })}
                  variant="accent"
                  className="dxp-wizard-cta-inverse"
                />
              </DxpFooterRow>
            </DxpMessageCard>
          </DxpNarrowColumn>
        </div>
      </DxpPageShell>
    </>
  );
}

export default AlreadyRegisteredWithAnotherProgram;
